import React, { useState, useEffect, useRef } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { fetchBadges, createBadge, updateBadge, deleteBadge, uploadBadgeArtwork } from '../../services/badgeService';

const BADGE_TYPES = ['xp', 'streak', 'quiz', 'reading', 'pronunciation', 'avatar', 'glossary'];

const ICON_OPTIONS = [
  'Star', 'Trophy', 'Award', 'Medal', 'Flame', 'TrendingUp', 'Zap', 'Shield',
  'Crown', 'Sparkles', 'BookOpen', 'Mic', 'Target', 'CheckCircle', 'Heart'
];

const RARITY_OPTIONS = ['common', 'rare', 'epic', 'legendary'];
const DIFFICULTY_OPTIONS = ['easy', 'medium', 'hard', 'legendary'];

// ── Rarity helpers ─────────────────────────────────────────────────────────────
export const RARITY_CONFIG = {
  common:    { label: 'Common',    color: 'text-gray-400',   bg: 'bg-gray-500/15',   border: 'border-gray-500/30',   ring: 'ring-gray-400/60' },
  rare:      { label: 'Rare',      color: 'text-blue-400',   bg: 'bg-blue-500/15',   border: 'border-blue-500/30',   ring: 'ring-blue-400/60' },
  epic:      { label: 'Epic',      color: 'text-purple-400', bg: 'bg-purple-500/15', border: 'border-purple-500/30', ring: 'ring-purple-400/60' },
  legendary: { label: 'Legendary', color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30', ring: 'ring-yellow-400/60' },
};

const DIFFICULTY_CONFIG = {
  easy:      { label: 'Easy',      color: 'text-green-400',  bg: 'bg-green-500/15',  border: 'border-green-500/30'  },
  medium:    { label: 'Medium',    color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30' },
  hard:      { label: 'Hard',      color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30' },
  legendary: { label: 'Legendary', color: 'text-red-400',    bg: 'bg-red-500/15',    border: 'border-red-500/30'    },
};

const RarityBadge = ({ rarity }) => {
  const cfg = RARITY_CONFIG?.[rarity] || RARITY_CONFIG?.common;
  const isLegendary = rarity === 'legendary';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg?.bg} ${cfg?.color} ${cfg?.border} ${isLegendary ? 'legendary-shimmer' : ''}`}>
      {isLegendary && <Icon name="Sparkles" size={10} />}
      {cfg?.label}
    </span>
  );
};

const emptyForm = {
  badge_name: '',
  badge_type: 'xp',
  badge_description: '',
  xp_threshold: 0,
  icon: 'Star',
  artwork_url: null,
  rarity: 'common',
  unlock_difficulty: 'easy',
};

const BadgeFormModal = ({ badge, onClose, onSave }) => {
  const [form, setForm] = useState(badge ? {
    badge_name: badge?.badge_name || '',
    badge_type: badge?.badge_type || 'xp',
    badge_description: badge?.badge_description || '',
    xp_threshold: badge?.xp_threshold || 0,
    icon: badge?.icon || 'Star',
    artwork_url: badge?.artwork_url || null,
    rarity: badge?.rarity || 'common',
    unlock_difficulty: badge?.unlock_difficulty || 'easy',
  } : { ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState(badge?.artwork_url || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleArtworkChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.type !== 'image/png' && file?.type !== 'image/jpeg' && file?.type !== 'image/webp') {
      setError('Please select a PNG, JPEG, or WebP image.');
      return;
    }
    if (file?.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB.');
      return;
    }
    setArtworkFile(file);
    setArtworkPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleRemoveArtwork = () => {
    setArtworkFile(null);
    setArtworkPreview(null);
    setForm(prev => ({ ...prev, artwork_url: null }));
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.badge_name?.trim()) { setError('Badge name is required.'); return; }
    if (!form?.badge_type?.trim()) { setError('Badge type is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      let artworkUrl = form?.artwork_url;
      if (artworkFile) {
        setUploading(true);
        artworkUrl = await uploadBadgeArtwork(artworkFile, badge?.id || null);
        setUploading(false);
      }
      await onSave({ ...form, artwork_url: artworkUrl });
      onClose();
    } catch (err) {
      setUploading(false);
      setError(err?.message || 'Failed to save badge.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {badge ? 'Edit Badge' : 'Create Badge'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Badge Name <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form?.badge_name}
              onChange={e => handleChange('badge_name', e?.target?.value)}
              placeholder="e.g. Week Warrior"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Badge Type <span className="text-red-400">*</span></label>
            <select
              value={form?.badge_type}
              onChange={e => handleChange('badge_type', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {BADGE_TYPES?.map(t => (
                <option key={t} value={t}>{t?.charAt(0)?.toUpperCase() + t?.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* Rarity + Difficulty row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Rarity Tier</label>
              <select
                value={form?.rarity}
                onChange={e => handleChange('rarity', e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {RARITY_OPTIONS?.map(r => (
                  <option key={r} value={r}>{r?.charAt(0)?.toUpperCase() + r?.slice(1)}</option>
                ))}
              </select>
              {form?.rarity && (
                <div className="mt-1.5">
                  <RarityBadge rarity={form?.rarity} />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Unlock Difficulty</label>
              <select
                value={form?.unlock_difficulty}
                onChange={e => handleChange('unlock_difficulty', e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {DIFFICULTY_OPTIONS?.map(d => (
                  <option key={d} value={d}>{d?.charAt(0)?.toUpperCase() + d?.slice(1)}</option>
                ))}
              </select>
              {form?.unlock_difficulty && (() => {
                const cfg = DIFFICULTY_CONFIG?.[form?.unlock_difficulty];
                return cfg ? (
                  <span className={`mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg?.bg} ${cfg?.color} ${cfg?.border}`}>
                    {cfg?.label}
                  </span>
                ) : null;
              })()}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
            <textarea
              value={form?.badge_description}
              onChange={e => handleChange('badge_description', e?.target?.value)}
              placeholder="Describe when this badge is awarded..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">XP Threshold</label>
            <input
              type="number"
              min="0"
              value={form?.xp_threshold}
              onChange={e => handleChange('xp_threshold', parseInt(e?.target?.value) || 0)}
              placeholder="0"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <p className="text-xs text-muted-foreground mt-1">Minimum XP required to earn this badge (0 = no XP requirement)</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Icon</label>
            <div className="grid grid-cols-5 gap-2">
              {ICON_OPTIONS?.map(iconName => (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => handleChange('icon', iconName)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs transition-all ${
                    form?.icon === iconName
                      ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <Icon name={iconName} size={18} />
                  <span className="truncate w-full text-center">{iconName}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Artwork Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Badge Artwork (PNG)</label>
            {artworkPreview ? (
              <div className="flex items-center gap-3">
                <img
                  src={artworkPreview}
                  alt="Badge artwork preview"
                  className="w-16 h-16 rounded-lg object-cover border border-border"
                />
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef?.current?.click()}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <Icon name="RefreshCw" size={12} />
                    Replace image
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveArtwork}
                    className="text-xs text-red-400 hover:underline flex items-center gap-1"
                  >
                    <Icon name="Trash2" size={12} />
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef?.current?.click()}
                className="w-full flex flex-col items-center gap-2 p-4 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
              >
                <Icon name="ImagePlus" size={22} />
                <span className="text-sm">Click to upload PNG artwork</span>
                <span className="text-xs">PNG, JPEG, WebP · Max 5MB</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleArtworkChange}
              className="hidden"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || uploading}>
              {uploading ? (
                <span className="flex items-center gap-2"><Icon name="Loader2" size={14} className="animate-spin" />Uploading…</span>
              ) : saving ? (
                <span className="flex items-center gap-2"><Icon name="Loader2" size={14} className="animate-spin" />Saving…</span>
              ) : (badge ? 'Save Changes' : 'Create Badge')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ badge, onClose, onConfirm }) => {
  const [deleting, setDeleting] = useState(false);
  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-sm shadow-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
            <Icon name="Trash2" size={18} className="text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Delete Badge</h3>
            <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
          </div>
        </div>
        <p className="text-sm text-foreground">
          Are you sure you want to delete <span className="font-medium">"{badge?.badge_name}"</span>?
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={deleting}>
            {deleting ? <span className="flex items-center gap-2"><Icon name="Loader2" size={14} className="animate-spin" />Deleting…</span> : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const BadgeManagement = () => {
  const { userProfile } = useAuth();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [rarityFilter, setRarityFilter] = useState('all');
  const [createModal, setCreateModal] = useState(false);
  const [editBadge, setEditBadge] = useState(null);
  const [deleteBadgeItem, setDeleteBadgeItem] = useState(null);

  const isSuperAdmin = userProfile?.role === 'superadmin';

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBadges();
      setBadges(data);
    } catch (err) {
      setError(err?.message || 'Failed to load badges.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (form) => {
    const newBadge = await createBadge(form);
    setBadges(prev => [newBadge, ...prev]);
  };

  const handleEdit = async (form) => {
    const updated = await updateBadge(editBadge?.id, form);
    setBadges(prev => prev?.map(b => b?.id === updated?.id ? updated : b));
  };

  const handleDelete = async () => {
    await deleteBadge(deleteBadgeItem?.id);
    setBadges(prev => prev?.filter(b => b?.id !== deleteBadgeItem?.id));
    setDeleteBadgeItem(null);
  };

  const filtered = badges?.filter(b => {
    const matchSearch = !search || b?.badge_name?.toLowerCase()?.includes(search?.toLowerCase()) || b?.badge_description?.toLowerCase()?.includes(search?.toLowerCase());
    const matchType = typeFilter === 'all' || b?.badge_type === typeFilter;
    const matchRarity = rarityFilter === 'all' || b?.rarity === rarityFilter;
    return matchSearch && matchType && matchRarity;
  });

  const uniqueTypes = [...new Set(badges.map(b => b.badge_type).filter(Boolean))];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbTrail />

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Icon name="Award" size={24} className="text-primary" />
                Badge Management
              </h1>
              <p className="text-muted-foreground mt-1">Create and manage badges awarded to students</p>
            </div>
            {isSuperAdmin && (
              <Button onClick={() => setCreateModal(true)} className="flex items-center gap-2">
                <Icon name="Plus" size={16} />
                Create Badge
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                placeholder="Search badges..."
                className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e?.target?.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="all">All Types</option>
              {uniqueTypes?.map(t => (
                <option key={t} value={t}>{t?.charAt(0)?.toUpperCase() + t?.slice(1)}</option>
              ))}
            </select>
            <select
              value={rarityFilter}
              onChange={e => setRarityFilter(e?.target?.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="all">All Rarities</option>
              {RARITY_OPTIONS?.map(r => (
                <option key={r} value={r}>{r?.charAt(0)?.toUpperCase() + r?.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Badges', value: badges?.length, icon: 'Award', color: 'text-primary' },
              { label: 'Legendary', value: badges?.filter(b => b?.rarity === 'legendary')?.length, icon: 'Sparkles', color: 'text-yellow-400' },
              { label: 'Epic', value: badges?.filter(b => b?.rarity === 'epic')?.length, icon: 'Crown', color: 'text-purple-400' },
              { label: 'Rare', value: badges?.filter(b => b?.rarity === 'rare')?.length, icon: 'Star', color: 'text-blue-400' },
            ]?.map(stat => (
              <div key={stat?.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
                <div className={`${stat?.color}`}><Icon name={stat?.icon} size={20} /></div>
                <div>
                  <p className="text-xl font-bold text-foreground">{stat?.value}</p>
                  <p className="text-xs text-muted-foreground">{stat?.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
              <Icon name="AlertCircle" size={24} className="text-red-400 mx-auto mb-2" />
              <p className="text-red-400 font-medium">{error}</p>
              <Button variant="outline" onClick={loadBadges} className="mt-3">Retry</Button>
            </div>
          ) : filtered?.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <Icon name="Award" size={40} className="text-muted-foreground mx-auto mb-3 opacity-40" />
              <p className="text-foreground font-medium mb-1">No badges found</p>
              <p className="text-muted-foreground text-sm">
                {search || typeFilter !== 'all' || rarityFilter !== 'all' ? 'Try adjusting your filters.' : 'Create your first badge to get started.'}
              </p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Badge</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Artwork</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Rarity</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden md:table-cell">Description</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Difficulty</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">XP</th>
                      {isSuperAdmin && (
                        <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered?.map(badge => {
                      const rarityCfg = RARITY_CONFIG?.[badge?.rarity] || RARITY_CONFIG?.common;
                      const difficultyCfg = DIFFICULTY_CONFIG?.[badge?.unlock_difficulty] || DIFFICULTY_CONFIG?.easy;
                      return (
                        <tr key={badge?.id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${rarityCfg?.bg} ${rarityCfg?.border}`}>
                                <Icon name={badge?.icon || 'Award'} size={18} className={rarityCfg?.color} />
                              </div>
                              <span className="font-medium text-foreground text-sm">{badge?.badge_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {badge?.artwork_url ? (
                              <img
                                src={badge?.artwork_url}
                                alt={`${badge?.badge_name} artwork`}
                                className="w-10 h-10 rounded-lg object-cover border border-border"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-muted/40 border border-border flex items-center justify-center">
                                <Icon name="ImageOff" size={14} className="text-muted-foreground" />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <RarityBadge rarity={badge?.rarity || 'common'} />
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20 capitalize">
                              {badge?.badge_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <p className="text-sm text-muted-foreground line-clamp-2 max-w-xs">{badge?.badge_description || '—'}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${difficultyCfg?.bg} ${difficultyCfg?.color} ${difficultyCfg?.border} capitalize`}>
                              {badge?.unlock_difficulty || 'easy'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-1 text-sm text-foreground">
                              <Icon name="Zap" size={13} className="text-yellow-400" />
                              {badge?.xp_threshold?.toLocaleString() || 0}
                            </span>
                          </td>
                          {isSuperAdmin && (
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => setEditBadge(badge)}
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                                  title="Edit badge"
                                >
                                  <Icon name="Pencil" size={15} />
                                </button>
                                <button
                                  onClick={() => setDeleteBadgeItem(badge)}
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                  title="Delete badge"
                                >
                                  <Icon name="Trash2" size={15} />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
                Showing {filtered?.length} of {badges?.length} badge{badges?.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>
      </div>
      {createModal && (
        <BadgeFormModal badge={null} onClose={() => setCreateModal(false)} onSave={handleCreate} />
      )}
      {editBadge && (
        <BadgeFormModal badge={editBadge} onClose={() => setEditBadge(null)} onSave={handleEdit} />
      )}
      {deleteBadgeItem && (
        <DeleteConfirmModal badge={deleteBadgeItem} onClose={() => setDeleteBadgeItem(null)} onConfirm={handleDelete} />
      )}
    </div>
  );
};

export default BadgeManagement;
